import React from 'react';
import './CreateJobAdvanced.scss';
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Typography
} from '@mui/material';
import { createJobConfigHooks } from './hooks';
import { JobDetailsCard } from './components/JobDetailsCard';
import { NotificationsCard } from './components/NotificationsCard';
import { CronScheduleCard } from './components/CronScheduleCard';
import { KronosAuthCard } from './components/KronosAuthCard';
import { SftpCredentialsCard } from './components/SftpCredentialsCard';
import { CommaListField } from './components/CommaListField';
import { CodeMappingTable } from './components/CodeMappingTable';
import {
  LRS_IMPORT_DEFAULTS,
  LRS_DEFAULT_FILE_NAME_PATTERN,
  LRS_DEFAULT_EARNING_LIST_NAME
} from './lrsConfig';

const { useJobGeneralConfig, useJobConfig } = createJobConfigHooks({
  partner: 'LRS',
  defaultConfig: LRS_IMPORT_DEFAULTS
});

const ClientPlanCard = () => {
  const [companyName, setCompanyName] = useJobConfig(['companyName']);
  const [planIds, setPlanIds] = useJobConfig(['planIds']);
  const [earningListName, setEarningListName] = useJobConfig(['earningListName']);
  const [searchCriteria, setSearchCriteria] = useJobConfig(['searchCriteriaToUpload']);
  return (
    <Card>
      <CardHeader subheader="Identifies which client this feed is for" title="Client & Plan" />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              helperText="UKG company short name, used in notifications"
              label="Company short name"
              onChange={(e) => setCompanyName(e.target.value)}
              required
              value={companyName || ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <CommaListField
              helperText="LRS PlanIDs (contract number + sub ID) that belong to this client. Comma separated."
              label="Plan IDs"
              onChange={(ids) => setPlanIds(ids)}
              placeholder="e.g. P68, P69"
              required
              value={planIds}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              helperText={`Defaults to '${LRS_DEFAULT_EARNING_LIST_NAME}'`}
              label="Earning list name"
              onChange={(e) => setEarningListName(e.target.value || undefined)}
              value={earningListName || ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              helperText="UKG company ID the deferral changes are uploaded to"
              label="Search criteria to upload: company ID"
              onChange={(e) => {
                const raw = e.target.value;
                const rest = { ...searchCriteria };
                delete rest.company_id;
                if (raw === '') {
                  setSearchCriteria(Object.keys(rest).length > 0 ? rest : undefined);
                  return;
                }
                const asNumber = Number(raw);
                if (!Number.isFinite(asNumber)) return;
                setSearchCriteria({ ...rest, company_id: asNumber });
              }}
              type="number"
              value={searchCriteria?.company_id ?? ''}
              variant="standard"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const FilesCard = () => {
  const [sourceDirectory, setSourceDirectory] = useJobConfig(['sourceDirectory']);
  const [archiveDirectory, setArchiveDirectory] = useJobConfig(['archiveDirectory']);
  const [fileNamePattern, setFileNamePattern] = useJobConfig(['fileNamePattern']);
  return (
    <Card>
      <CardHeader subheader="Where the LRS deferral files are read from, and what happens to them" title="SFTP files" />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              helperText="The job reads incoming files from this folder. Blank reads the SFTP home folder."
              label="Source directory"
              onChange={(e) => setSourceDirectory(e.target.value || undefined)}
              value={sourceDirectory || ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              helperText="The job moves the files here after processing them"
              label="Archive directory"
              onChange={(e) => setArchiveDirectory(e.target.value)}
              value={archiveDirectory ?? ''}
              variant="standard"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              helperText={`Regular expression for the files to pick up (case-insensitive). Defaults to ${LRS_DEFAULT_FILE_NAME_PATTERN}`}
              label="File name pattern"
              onChange={(e) => setFileNamePattern(e.target.value || undefined)}
              value={fileNamePattern || ''}
              variant="standard"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const DeductionMappingCard = () => {
  const [deductionCodes, setDeductionCodes] = useJobConfig(['deductionCodes']);
  const [loanDeductionCodes, setLoanDeductionCodes] = useJobConfig(['loanDeductionCodes']);
  return (
    <Card>
      <CardHeader
        subheader="Maps LRS codes to Wurk deductions. 401K maps to the 401K deduction when it is not listed."
        title="Deduction mapping"
      />
      <CardContent>
        <Typography variant="subtitle2">Deduction codes</Typography>
        <CodeMappingTable
          codeLabel="Contribution code"
          codePlaceholder="e.g. 4ROTH"
          nameLabel="Deduction name"
          namePlaceholder="e.g. Roth 401K"
          onChange={setDeductionCodes}
          value={deductionCodes}
        />
        <Typography sx={{ marginTop: 3 }} variant="subtitle2">Loan deduction codes</Typography>
        <CodeMappingTable
          codeLabel="Loan number"
          codePlaceholder="e.g. 1"
          nameLabel="Loan deduction name"
          namePlaceholder="e.g. 401K Loan 1"
          onChange={setLoanDeductionCodes}
          value={loanDeductionCodes}
        />
      </CardContent>
    </Card>
  );
};

const SlackCard = () => {
  const [slackSimplified, setSlackSimplified] = useJobConfig(['slackSimplified']);
  return (
    <Card>
      <CardHeader subheader="How this job's results are posted in Slack" title="Slack" />
      <CardContent>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!slackSimplified}
              onChange={(e) => setSlackSimplified(e.target.checked)}
            />
          }
          label="Slack simplified"
        />
        <FormHelperText>Post only the job name and the result, without the run details.</FormHelperText>
      </CardContent>
    </Card>
  );
};

export const LrsImportForm = () => {
  const [sourceDirectory] = useJobConfig(['sourceDirectory']);
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <JobDetailsCard useJobGeneralConfig={useJobGeneralConfig} />
      </Grid>
      <Grid item xs={12}>
        <ClientPlanCard />
      </Grid>
      <Grid item xs={12}>
        <KronosAuthCard useJobConfig={useJobConfig} />
      </Grid>
      <Grid item xs={12}>
        <SftpCredentialsCard
          subheader="Where LRS drops the deferral change files"
          testDirectory={sourceDirectory}
          useJobConfig={useJobConfig}
        />
      </Grid>
      <Grid item xs={12}>
        <FilesCard />
      </Grid>
      <Grid item xs={12}>
        <DeductionMappingCard />
      </Grid>
      <Grid item xs={12}>
        <NotificationsCard useJobConfig={useJobConfig} />
      </Grid>
      <Grid item xs={12}>
        <SlackCard />
      </Grid>
      <Grid item xs={12}>
        <CronScheduleCard useJobGeneralConfig={useJobGeneralConfig} />
      </Grid>
    </Grid>
  );
};
